import { supabase } from '@/integrations/supabase/client';
import { emitTicketsChanged } from '@/lib/tickets-events';
import { getCurrentUserId } from '@/services/data/auth';
import { getUserRoles, type UserRoleType } from '@/services/data/roles';

export type TicketTeam = 'ops' | 'pbm' | 'video_editor' | 'monteur' | 'admin' | 'cm';
export type TicketPriority = 'urgent' | 'medium' | 'normal';

// Roles that have full visibility on all tickets (Ops + admins)
const OPS_ROLES: Set<UserRoleType> = new Set(['ops_assistant', 'ops', 'admin', 'super_admin']);

// Mapping from user role to assigned_team value
const ROLE_TO_TEAM: Partial<Record<UserRoleType, TicketTeam>> = {
  pbm: 'pbm',
  virality_expert: 'video_editor',
  monteur: 'monteur',
  admin: 'admin',
  super_admin: 'admin',
  ops_assistant: 'ops',
  ops: 'ops',
  concierge: 'admin',
};

// Mapping from ticket team to customer staff assignment field
// Used to filter tickets so staff only see tickets for their assigned customers
const TEAM_TO_CUSTOMER_FIELD: Record<TicketTeam, string | null> = {
  ops: 'ops_id',
  pbm: 'personal_brand_manager_id',
  video_editor: 'video_editor_id',
  monteur: 'monteur_id',
  admin: null, // Admins see all tickets via OPS_ROLES
  cm: null, // CM has no direct customer assignment field
};

export const TICKET_TEAMS: { value: TicketTeam; label: string }[] = [
  { value: 'pbm', label: 'Personal Brand Manager' },
  { value: 'ops', label: 'Équipe Ops' },
  { value: 'cm', label: 'Community Management' },
  { value: 'video_editor', label: 'Virality Expert' },
  { value: 'monteur', label: 'Monteur' },
  { value: 'admin', label: 'Conciergerie' },
];

export const TICKET_PRIORITIES: { value: TicketPriority; label: string; description: string; color: string }[] = [
  { value: 'urgent', label: 'Urgent', description: 'À traiter dans l\'heure', color: 'destructive' },
  { value: 'medium', label: 'Important', description: 'À traiter dans la journée', color: 'warning' },
  { value: 'normal', label: 'Classique', description: 'À traiter sous 24h', color: 'secondary' },
];

export interface TicketNoteData {
  id: string;
  ticket_id: string;
  author_id: string;
  content: string;
  mentioned_user_ids: string[];
  created_at: string;
  updated_at: string;
  author?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export interface TicketReplyData {
  id: string;
  content: string | null;
  sender_id: string;
  created_at: string;
  sender?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export interface ChatTicket {
  id: string;
  conversation_id: string;
  message_id: string;
  selected_text: string;
  title: string | null;
  internal_note: string | null;
  assigned_team: TicketTeam;
  priority: TicketPriority;
  status: 'open' | 'closed';
  created_by: string;
  created_at: string;
  closed_at: string | null;
  closed_by: string | null;
  suggested_responses: string[] | null;
  // Joined data
  conversation?: {
    id: string;
    customer_id: string;
    customer?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      email: string;
      avatar_url: string | null;
    };
  };
  message?: {
    id: string;
    content: string | null;
    sender_id: string;
    created_at: string;
    sender?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      avatar_url: string | null;
    };
  };
  creator?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
  // Preloaded notes
  notes?: TicketNoteData[];
  // Preloaded replies sent from ticket interface
  replies?: TicketReplyData[];
  // Resolved assignee (person in charge based on team + customer staff assignments)
  assignee?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
}

/**
 * Create a new ticket from a chat message
 */
export async function createTicket(params: {
  conversationId: string;
  messageId: string;
  selectedText: string;
  assignedTeam: TicketTeam;
  priority?: TicketPriority;
  title?: string;
  internalNote?: string;
  suggestedResponses?: string[];
}): Promise<{ data: ChatTicket | null; error: string | null }> {
  const userId = await getCurrentUserId();
  if (!userId) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('chat_tickets')
    .insert({
      conversation_id: params.conversationId,
      message_id: params.messageId,
      selected_text: params.selectedText,
      assigned_team: params.assignedTeam,
      priority: params.priority || 'normal',
      title: params.title || null,
      internal_note: params.internalNote || null,
      suggested_responses: params.suggestedResponses || [],
      created_by: userId,
    })
    .select('*')
    .single();

  if (error) return { data: null, error: error.message };
  emitTicketsChanged();
  
  // Return raw ticket data - full reload will happen via Realtime subscription
  return { data: data as ChatTicket, error: null };
}

/**
 * Get a ticket by ID with all relations
 */
export async function getTicketById(ticketId: string): Promise<{ data: ChatTicket | null; error: string | null }> {
  const { data: ticket, error: ticketError } = await supabase
    .from('chat_tickets')
    .select('*')
    .eq('id', ticketId)
    .single();

  if (ticketError) return { data: null, error: ticketError.message };

  // Fetch conversation with customer
  const { data: conversation } = await supabase
    .from('conversations')
    .select(`
      id, customer_id,
      customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
    `)
    .eq('id', ticket.conversation_id)
    .single();

  // Fetch message with sender
  const { data: message } = await supabase
    .from('chat_messages')
    .select(`
      id, content, sender_id, created_at,
      sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .eq('id', ticket.message_id)
    .single();

  // Fetch creator
  const { data: creator } = await supabase
    .from('users')
    .select('id, first_name, last_name, avatar_url')
    .eq('id', ticket.created_by)
    .single();

  return {
    data: {
      ...ticket,
      conversation: conversation || undefined,
      message: message || undefined,
      creator: creator || undefined,
    } as ChatTicket,
    error: null,
  };
}

/**
 * Get tickets for the current user based on their team role
 * Also includes tickets where the user is mentioned in notes
 * Optimized: Single query with all joins instead of multiple sequential requests
 * Sorted by: priority (urgent first), then by created_at (oldest first)
 *
 * IMPORTANT: Non-ops staff only see tickets where:
 * 1. They are the assigned staff member for that customer AND the ticket team matches their role
 * 2. They are mentioned in a note on the ticket
 * 3. They created the ticket
 */
export async function getMyTickets(status: 'open' | 'closed' = 'open', effectiveUserId?: string): Promise<{ data: ChatTicket[]; error: string | null }> {
  const authUserId = await getCurrentUserId();
  if (!authUserId) return { data: [], error: 'Not authenticated' };

  const filterUserId = effectiveUserId ?? authUserId;

  // Get user's roles to determine visibility
  const { data: userRoles } = await getUserRoles(filterUserId);
  const roles = userRoles || [];
  const isOps = roles.some(r => OPS_ROLES.has(r));

  // Get tickets where user is mentioned in notes
  const { data: mentionNotes } = await supabase
    .from('chat_ticket_notes')
    .select('ticket_id')
    .contains('mentioned_user_ids', [filterUserId]);

  const mentionedTicketIds = mentionNotes ? [...new Set(mentionNotes.map(n => n.ticket_id))] : [];

  // Determine which teams the user belongs to
  const userTeams = [...new Set(roles.map(r => ROLE_TO_TEAM[r]).filter(Boolean))] as TicketTeam[];

  // For PBM users, we need to lookup their PBM record ID (since personal_brand_manager_id
  // in users table references team_personal_brand_managers.id, not users.id)
  let userPbmRecordId: string | null = null;
  if (userTeams.includes('pbm')) {
    const { data: pbmRecord } = await supabase
      .from('team_personal_brand_managers')
      .select('id')
      .eq('user_id', authUserId)
      .limit(1)
      .maybeSingle();
    userPbmRecordId = pbmRecord?.id || null;
  }

  // First get all tickets with the given status, including notes and customer staff assignments
  const { data: tickets, error } = await supabase
    .from('chat_tickets')
    .select(`
      *,
      conversation:conversations!chat_tickets_conversation_id_fkey(
        id, customer_id,
        customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url, ops_id, personal_brand_manager_id, video_editor_id, monteur_id)
      ),
      message:chat_messages!chat_tickets_message_id_fkey(
        id, content, sender_id, created_at,
        sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
      ),
      notes:chat_ticket_notes(
        id, ticket_id, author_id, content, mentioned_user_ids, created_at, updated_at
      )
    `)
    .eq('status', status);

  if (error) return { data: [], error: error.message };
  if (!tickets || tickets.length === 0) return { data: [], error: null };

  // Helper function to check if user is assigned to a customer for a specific team
  const isAssignedToCustomerForTeam = (customer: any, team: TicketTeam): boolean => {
    if (!customer) return false;
    const staffField = TEAM_TO_CUSTOMER_FIELD[team];
    if (!staffField) return true; // Teams without customer assignment field (admin/cm) have no restriction

    const assignedStaffId = customer[staffField];
    if (!assignedStaffId) return false;

    if (team === 'pbm') {
      return userPbmRecordId !== null && assignedStaffId === userPbmRecordId;
    }
    return assignedStaffId === authUserId;
  };

  // Filter tickets based on role visibility
  const visibleTickets = isOps
    ? tickets
    : tickets.filter(ticket => {
        const customer = ticket.conversation?.customer;
        const ticketTeam = ticket.assigned_team as TicketTeam;

        // Check if user is assigned to this customer for the ticket's team
        const isAssignedForTeam = userTeams.includes(ticketTeam) &&
          isAssignedToCustomerForTeam(customer, ticketTeam);

        return isAssignedForTeam ||
          // User is mentioned in a note on this ticket
          mentionedTicketIds.includes(ticket.id) ||
          // User created the ticket
          ticket.created_by === filterUserId;
      });

  if (visibleTickets.length === 0) return { data: [], error: null };

  // Fetch replies sent from ticket interface (messages with source_ticket_id)
  const ticketIds = visibleTickets.map(t => t.id);
  const { data: replies } = await supabase
    .from('chat_messages')
    .select(`
      id, content, sender_id, created_at, source_ticket_id,
      sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .in('source_ticket_id', ticketIds)
    .order('created_at', { ascending: true });

  // Group replies by ticket_id
  const repliesByTicket = new Map<string, TicketReplyData[]>();
  (replies || []).forEach((reply: any) => {
    const ticketId = reply.source_ticket_id;
    const existingReplies = repliesByTicket.get(ticketId) ?? [];
    existingReplies.push({
      id: reply.id,
      content: reply.content,
      sender_id: reply.sender_id,
      created_at: reply.created_at,
      sender: reply.sender,
    });
    repliesByTicket.set(ticketId, existingReplies);
  });

  // Fetch creators separately (no direct FK on chat_tickets to users for created_by)
  const creatorIds = [...new Set(visibleTickets.map(t => t.created_by))];
  
  // Also fetch note authors
  const noteAuthorIds = new Set<string>();
  visibleTickets.forEach(t => {
    if (t.notes) {
      t.notes.forEach((n: { author_id: string }) => noteAuthorIds.add(n.author_id));
    }
  });
  
  const allUserIds = [...new Set([...creatorIds, ...noteAuthorIds])];
  const { data: users } = await supabase
    .from('users')
    .select('id, first_name, last_name, avatar_url')
    .in('id', allUserIds);

  const usersMap = new Map((users || []).map(u => [u.id, u]));

  // Priority order: urgent = 1, medium = 2, normal = 3
  const priorityOrder: Record<TicketPriority, number> = {
    urgent: 1,
    medium: 2,
    normal: 3,
  };

  const enrichedTickets = (visibleTickets as unknown as (ChatTicket & { isMentioned?: boolean })[]).map(ticket => ({
    ...ticket,
    creator: usersMap.get(ticket.created_by),
    // Mark if user is mentioned in this ticket
    isMentioned: mentionedTicketIds.includes(ticket.id),
    // Enrich notes with author info and sort by created_at
    notes: (ticket.notes || [])
      .map((note: { author_id: string; created_at: string }) => ({
        ...note,
        author: usersMap.get(note.author_id),
      }))
      .sort((a: { created_at: string }, b: { created_at: string }) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    // Add replies for this ticket
    replies: repliesByTicket.get(ticket.id) || [],
  })) as (ChatTicket & { isMentioned?: boolean })[];

  // Sort by priority (urgent first), then by created_at (oldest first)
  enrichedTickets.sort((a, b) => {
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    // Same priority: oldest first (ascending order)
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  return { data: enrichedTickets, error: null };
}

/**
 * Get tickets by message IDs (for displaying ticket badges in chat)
 */
export async function getTicketsByMessageIds(messageIds: string[]): Promise<Record<string, ChatTicket[]>> {
  if (!messageIds || messageIds.length === 0) return {};

  const { data: tickets, error } = await supabase
    .from('chat_tickets')
    .select('*')
    .in('message_id', messageIds);

  if (error || !tickets) return {};

  // Group tickets by message_id
  const ticketsByMessage: Record<string, ChatTicket[]> = {};
  for (const ticket of tickets) {
    if (!ticketsByMessage[ticket.message_id]) {
      ticketsByMessage[ticket.message_id] = [];
    }
    ticketsByMessage[ticket.message_id].push(ticket as ChatTicket);
  }

  return ticketsByMessage;
}

/**
 * Get count of open tickets visible to the current user (role-based)
 * Non-ops staff only see tickets where they are assigned to the customer
 */
export async function getOpenTicketCount(effectiveUserId?: string): Promise<number> {
  const authUserId = await getCurrentUserId();
  if (!authUserId) return 0;

  const filterUserId = effectiveUserId ?? authUserId;

  const { data: userRoles } = await getUserRoles(filterUserId);
  const roles = userRoles || [];
  const isOps = roles.some(r => OPS_ROLES.has(r));

  if (isOps) {
    // Ops see all open tickets
    const { count, error } = await supabase
      .from('chat_tickets')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'open');
    if (error) return 0;
    return count || 0;
  }

  // For non-ops: fetch tickets with customer assignments then filter
  const userTeams = [...new Set(roles.map(r => ROLE_TO_TEAM[r]).filter(Boolean))] as TicketTeam[];

  // For PBM users, lookup their PBM record ID
  let userPbmRecordId: string | null = null;
  if (userTeams.includes('pbm')) {
    const { data: pbmRecord } = await supabase
      .from('team_personal_brand_managers')
      .select('id')
      .eq('user_id', filterUserId)
      .limit(1)
      .maybeSingle();
    userPbmRecordId = pbmRecord?.id || null;
  }

  const [ticketsResult, mentionResult] = await Promise.all([
    supabase
      .from('chat_tickets')
      .select(`
        id, assigned_team, created_by,
        conversation:conversations!chat_tickets_conversation_id_fkey(
          customer:users!conversations_customer_id_fkey(ops_id, personal_brand_manager_id, video_editor_id, monteur_id)
        )
      `)
      .eq('status', 'open'),
    supabase.from('chat_ticket_notes').select('ticket_id').contains('mentioned_user_ids', [filterUserId]),
  ]);

  if (ticketsResult.error || !ticketsResult.data) return 0;

  const mentionedIds = new Set((mentionResult.data || []).map(n => n.ticket_id));

  const isAssignedToCustomerForTeam = (customer: any, team: TicketTeam): boolean => {
    if (!customer) return false;
    const staffField = TEAM_TO_CUSTOMER_FIELD[team];
    if (!staffField) return true; // Teams without customer assignment field (admin/cm) have no restriction

    const assignedStaffId = customer[staffField];
    if (!assignedStaffId) return false;

    if (team === 'pbm') {
      return userPbmRecordId !== null && assignedStaffId === userPbmRecordId;
    }
    return assignedStaffId === filterUserId;
  };

  const visibleCount = ticketsResult.data.filter(t => {
    const customer = (t as any).conversation?.customer;
    const ticketTeam = t.assigned_team as TicketTeam;

    const isAssignedForTeam = userTeams.includes(ticketTeam) &&
      isAssignedToCustomerForTeam(customer, ticketTeam);

    return isAssignedForTeam ||
      mentionedIds.has(t.id) ||
      t.created_by === filterUserId;
  }).length;

  return visibleCount;
}

/**
 * Check if there is unseen ticket activity since a given timestamp.
 * Returns true if any visible ticket or ticket note was created/updated after `sinceIso`.
 * Non-ops staff only see tickets where they are assigned to the customer.
 */
export async function hasUnseenTicketActivity(sinceIso: string, effectiveUserId?: string): Promise<boolean> {
  const authUserId = await getCurrentUserId();
  if (!authUserId) return false;

  const filterUserId = effectiveUserId ?? authUserId;

  const { data: userRoles } = await getUserRoles(filterUserId);
  const roles = userRoles || [];
  const isOps = roles.some(r => OPS_ROLES.has(r));
  const userTeams = [...new Set(roles.map(r => ROLE_TO_TEAM[r]).filter(Boolean))] as TicketTeam[];

  // For PBM users, lookup their PBM record ID
  let userPbmRecordId: string | null = null;
  if (userTeams.includes('pbm')) {
    const { data: pbmRecord } = await supabase
      .from('team_personal_brand_managers')
      .select('id')
      .eq('user_id', filterUserId)
      .limit(1)
      .maybeSingle();
    userPbmRecordId = pbmRecord?.id || null;
  }

  // Check for new open tickets since timestamp
  const { data: recentTickets } = await supabase
    .from('chat_tickets')
    .select(`
      id, assigned_team, created_by, created_at,
      conversation:conversations!chat_tickets_conversation_id_fkey(
        customer:users!conversations_customer_id_fkey(ops_id, personal_brand_manager_id, video_editor_id, monteur_id)
      )
    `)
    .eq('status', 'open')
    .gt('created_at', sinceIso)
    .limit(50);

  if (recentTickets && recentTickets.length > 0) {
    if (isOps) return true;

    const isAssignedToCustomerForTeam = (customer: any, team: TicketTeam): boolean => {
      if (!customer) return false;
      const staffField = TEAM_TO_CUSTOMER_FIELD[team];
      if (!staffField) return true; // Teams without customer assignment field (admin/cm) have no restriction

      const assignedStaffId = customer[staffField];
      if (!assignedStaffId) return false;

      if (team === 'pbm') {
        return userPbmRecordId !== null && assignedStaffId === userPbmRecordId;
      }
      return assignedStaffId === filterUserId;
    };

    const hasVisible = recentTickets.some(t => {
      const customer = (t as any).conversation?.customer;
      const ticketTeam = t.assigned_team as TicketTeam;

      const isAssignedForTeam = userTeams.includes(ticketTeam) &&
        isAssignedToCustomerForTeam(customer, ticketTeam);

      return isAssignedForTeam || t.created_by === filterUserId;
    });
    if (hasVisible) return true;
  }

  // Check for new notes mentioning the user since timestamp
  const { count } = await supabase
    .from('chat_ticket_notes')
    .select('id', { count: 'exact', head: true })
    .contains('mentioned_user_ids', [filterUserId])
    .gt('created_at', sinceIso);

  return (count || 0) > 0;
}

/**
 * Update a ticket's fields
 */
export async function updateTicket(
  ticketId: string,
  params: {
    title?: string | null;
    assignedTeam?: TicketTeam;
    priority?: TicketPriority;
    internalNote?: string | null;
  }
): Promise<{ error: string | null }> {
  const updateData: Record<string, unknown> = {};
  
  if (params.title !== undefined) updateData.title = params.title;
  if (params.assignedTeam !== undefined) updateData.assigned_team = params.assignedTeam;
  if (params.priority !== undefined) updateData.priority = params.priority;
  if (params.internalNote !== undefined) updateData.internal_note = params.internalNote;

  if (Object.keys(updateData).length === 0) {
    return { error: null };
  }

  const { error } = await supabase
    .from('chat_tickets')
    .update(updateData)
    .eq('id', ticketId);

  return { error: error?.message || null };
}

/**
 * Close a ticket
 */
export async function closeTicket(ticketId: string): Promise<{ error: string | null }> {
  const userId = await getCurrentUserId();
  if (!userId) return { error: 'Not authenticated' };

  const { error } = await supabase
    .from('chat_tickets')
    .update({
      status: 'closed',
      closed_at: new Date().toISOString(),
      closed_by: userId,
    })
    .eq('id', ticketId);

  if (!error) emitTicketsChanged();
  return { error: error?.message || null };
}

/**
 * Reopen a ticket
 */
export async function reopenTicket(ticketId: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('chat_tickets')
    .update({
      status: 'open',
      closed_at: null,
      closed_by: null,
    })
    .eq('id', ticketId);

  if (!error) emitTicketsChanged();
  return { error: error?.message || null };
}

/**
 * Subscribe to ticket updates with debounce to prevent request storms
 */
export function subscribeToTickets(onUpdate: () => void) {
  let debounceTimer: NodeJS.Timeout | null = null;
  
  const debouncedUpdate = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(onUpdate, 300);
  };
  
  // Use unique channel name per subscription to avoid conflicts
  const channelId = `tickets:all:${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  
  const channel = supabase
    .channel(channelId)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'chat_tickets',
      },
      () => {
        debouncedUpdate();
      }
    )
    .subscribe();

  return () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to ticket updates for a single conversation and return affected message ids.
 * This avoids re-fetching tickets for *all* messages on every change.
 */
export function subscribeToConversationTickets(
  conversationId: string,
  onUpdate: (messageIds: string[]) => void,
  debounceMs: number = 200
) {
  let debounceTimer: NodeJS.Timeout | null = null;
  const pendingMessageIds = new Set<string>();

  const flush = () => {
    const ids = Array.from(pendingMessageIds);
    pendingMessageIds.clear();
    if (ids.length > 0) onUpdate(ids);
  };

  const scheduleFlush = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flush, debounceMs);
  };

  const channel = supabase
    .channel(`tickets:conv:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'chat_tickets',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload: any) => {
        const messageId = payload?.new?.message_id ?? payload?.old?.message_id;
        if (typeof messageId === 'string' && messageId.length > 0) {
          pendingMessageIds.add(messageId);
          scheduleFlush();
        }
      }
    )
    .subscribe();

  return () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    supabase.removeChannel(channel);
  };
}
